import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Stack,
    Typography,
    useTheme,
    alpha,
    Chip,
} from "@mui/material";
import { lighten, darken } from "@mui/material/styles";
import {
    PhoneIphoneRounded,
    ArrowBackRounded,
    ContentCopyRounded,
    CheckRounded,
    IosShareRounded,
    AddBoxRounded,
    MoreHorizRounded,
    TouchAppRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { FONTS, CARD_STYLES } from "../constants/styles";
import { PWAUtils } from "../utils/PWAUtils";
import Brand from "../api/Brand";

type BrowserTab = "safari" | "chrome" | "other";

const IOSInstallGuide: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const primary = theme.palette.primary.main;
    const [cookies] = useCookies(["uuid", "brand_id"]);
    const [brandInfo, setBrandInfo] = useState<{ name?: string; logo?: string | null }>({});

    useEffect(() => {
        const { uuid, brand_id } = cookies;
        if (!uuid || !brand_id) return;
        Brand.info(uuid, brand_id)
            .then((res) => setBrandInfo({ name: res?.name, logo: res?.logo }))
            .catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cookies.uuid, cookies.brand_id]);

    const detectedBrowser = useMemo(() => PWAUtils.getBrowserName(), []);
    const needsSafariOnly = useMemo(() => PWAUtils.needsSafariOnly(), []);
    const isInSafari = detectedBrowser === "safari";

    const initialTab: BrowserTab =
        detectedBrowser === "chrome"
            ? "chrome"
            : detectedBrowser === "firefox" ||
              detectedBrowser === "edge" ||
              detectedBrowser === "other"
            ? "other"
            : "safari";

    const [activeTab, setActiveTab] = useState<BrowserTab>(initialTab);
    const [copied, setCopied] = useState(false);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.origin);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // Fallback for older browsers
            const input = document.createElement("input");
            input.value = window.location.origin;
            document.body.appendChild(input);
            input.select();
            document.execCommand("copy");
            document.body.removeChild(input);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        }
    };

    const browserLabel =
        detectedBrowser === "safari"
            ? "Safari"
            : detectedBrowser === "chrome"
            ? "Chrome"
            : detectedBrowser === "firefox"
            ? "Firefox"
            : detectedBrowser === "edge"
            ? "Edge"
            : "this browser";

    // Show "open in Safari" state for old iOS + non-Safari
    const showSafariRedirect = needsSafariOnly && !isInSafari;

    return (
        <Stack
            className="force-dynamic-translation"
            direction="column"
            width="100%"
            flex={1}
            sx={{
                overflowY: "auto",
                overflowX: "hidden",
                bgcolor: "white",
            }}
        >
            {/* Hero */}
            <Stack
                width="100%"
                sx={{
                    background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                    pt: "56px",
                    pb: "48px",
                    px: 2,
                    boxSizing: "border-box",
                    position: "relative",
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        opacity: 0.04,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "repeat",
                        backgroundSize: "128px 128px",
                        pointerEvents: "none",
                    },
                }}
                alignItems="center"
                spacing={1.5}
            >
                {/* Back button */}
                <Box
                    onClick={() => navigate(-1)}
                    sx={{
                        position: "absolute",
                        top: "16px",
                        left: "16px",
                        width: "36px",
                        height: "36px",
                        borderRadius: "10px",
                        bgcolor: "rgba(255,255,255,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        zIndex: 2,
                        transition: "background 0.2s",
                        "&:active": { bgcolor: "rgba(255,255,255,0.25)" },
                    }}
                >
                    <ArrowBackRounded
                        sx={{ color: "white", fontSize: "20px" }}
                    />
                </Box>

                {/* Icon */}
                <Box
                    sx={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "16px",
                        bgcolor: "rgba(255,255,255,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        zIndex: 1,
                    }}
                >
                    <PhoneIphoneRounded
                        sx={{ color: "white", fontSize: "28px" }}
                    />
                </Box>

                <Typography
                    fontFamily={FONTS.display}
                    fontWeight={700}
                    fontSize="24px"
                    lineHeight="120%"
                    color="white"
                    textAlign="center"
                    zIndex={1}
                >
                    Install the App
                </Typography>

                <Typography
                    fontFamily={FONTS.body}
                    fontSize="14px"
                    lineHeight="150%"
                    color="rgba(255,255,255,0.8)"
                    textAlign="center"
                    zIndex={1}
                >
                    Add to your home screen in a few taps
                </Typography>

                {/* Browser detection badge */}
                <Chip
                    label={`You're using ${browserLabel}`}
                    size="small"
                    sx={{
                        bgcolor: "rgba(255,255,255,0.15)",
                        color: "white",
                        fontFamily: FONTS.body,
                        fontSize: "12px",
                        fontWeight: 500,
                        height: "28px",
                        zIndex: 1,
                        "& .MuiChip-label": { px: 1.5 },
                    }}
                />
            </Stack>

            {/* Content sheet */}
            <Stack
                sx={{
                    bgcolor: "white",
                    borderTopLeftRadius: "24px",
                    borderTopRightRadius: "24px",
                    mt: "-24px",
                    position: "relative",
                    zIndex: 1,
                    px: 2,
                    pt: 3,
                    pb: "100px",
                    "@keyframes fadeSlideUp": {
                        from: { opacity: 0, transform: "translateY(16px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                }}
                spacing={2.5}
            >
                {showSafariRedirect ? (
                    <SafariRedirectState
                        primary={primary}
                        browserLabel={browserLabel}
                        copied={copied}
                        onCopy={handleCopyLink}
                    />
                ) : (
                    <>
                        {/* Browser tabs (only if iOS 16.4+ and not forced to Safari) */}
                        {!needsSafariOnly && !isInSafari && (
                            <BrowserTabs
                                active={activeTab}
                                onChange={setActiveTab}
                                primary={primary}
                            />
                        )}

                        {/* Steps */}
                        {(isInSafari || needsSafariOnly || activeTab === "safari") && (
                            <SafariSteps primary={primary} brand={brandInfo} />
                        )}
                        {!isInSafari && !needsSafariOnly && activeTab === "chrome" && (
                            <ChromeSteps primary={primary} brand={brandInfo} />
                        )}
                        {!isInSafari && !needsSafariOnly && activeTab === "other" && (
                            <OtherBrowserSteps primary={primary} brand={brandInfo} />
                        )}
                    </>
                )}
            </Stack>
        </Stack>
    );
};

// ─── Safari Redirect State (iOS < 16.4 + non-Safari) ────────────────────────

const SafariRedirectState: React.FC<{
    primary: string;
    browserLabel: string;
    copied: boolean;
    onCopy: () => void;
}> = ({ primary, browserLabel, copied, onCopy }) => (
    <Stack
        spacing={2.5}
        sx={{
            animation: "fadeSlideUp 0.4s ease-out both",
        }}
    >
        {/* Explanation card */}
        <Stack
            sx={{
                ...CARD_STYLES,
                background: `linear-gradient(135deg, ${alpha(primary, 0.04)} 0%, ${alpha(lighten(primary, 0.3), 0.08)} 100%)`,
                p: 2.5,
            }}
            spacing={2}
            alignItems="center"
        >
            <Box
                sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "14px",
                    bgcolor: alpha(primary, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                }}
            >
                <IosShareRounded
                    sx={{ color: primary, fontSize: "24px" }}
                />
            </Box>

            <Typography
                fontFamily={FONTS.display}
                fontWeight={700}
                fontSize="18px"
                lineHeight="130%"
                color="text.primary"
                textAlign="center"
            >
                Safari Required
            </Typography>

            <Typography
                fontFamily={FONTS.body}
                fontSize="14px"
                lineHeight="150%"
                color="text.secondary"
                textAlign="center"
            >
                Your iOS version only supports installing apps from Safari.{" "}
                {browserLabel} can't add apps to your home screen yet.
            </Typography>
        </Stack>

        {/* Copy link button */}
        <Stack
            onClick={onCopy}
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{
                ...CARD_STYLES,
                p: 2,
                cursor: "pointer",
                bgcolor: copied ? alpha("#4caf50", 0.06) : alpha(primary, 0.06),
                transition: "all 0.3s ease",
                "&:active": { transform: "scale(0.98)" },
            }}
        >
            {copied ? (
                <CheckRounded sx={{ color: "#4caf50", fontSize: "20px" }} />
            ) : (
                <ContentCopyRounded
                    sx={{ color: primary, fontSize: "20px" }}
                />
            )}
            <Typography
                fontFamily={FONTS.display}
                fontWeight={600}
                fontSize="15px"
                color={copied ? "#4caf50" : primary}
            >
                {copied ? "Link Copied!" : "Copy Link"}
            </Typography>
        </Stack>

        {/* Redirect steps */}
        <Stack spacing={0}>
            <Typography
                fontFamily={FONTS.body}
                fontWeight={600}
                fontSize="11px"
                color="text.secondary"
                textTransform="uppercase"
                letterSpacing="0.6px"
                sx={{ mb: 1.5 }}
            >
                Then follow these steps
            </Typography>

            <MiniStep number={1} text="Tap Copy Link above" primary={primary} delay={0} />
            <MiniStep number={2} text="Open the Safari app" primary={primary} delay={1} />
            <MiniStep number={3} text="Paste the link and go to the page" primary={primary} delay={2} />
            <MiniStep
                number={4}
                text="Follow the install steps shown there"
                primary={primary}
                delay={3}
                isLast
            />
        </Stack>
    </Stack>
);

const MiniStep: React.FC<{
    number: number;
    text: string;
    primary: string;
    delay: number;
    isLast?: boolean;
}> = ({ number, text, primary, delay, isLast }) => (
    <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
            py: 1.25,
            borderBottom: isLast ? "none" : "1px solid",
            borderColor: "rgba(0,0,0,0.06)",
            animation: "fadeSlideUp 0.35s ease-out both",
            animationDelay: `${0.1 + delay * 0.08}s`,
        }}
    >
        <Box
            sx={{
                width: "28px",
                height: "28px",
                borderRadius: "8px",
                bgcolor: alpha(primary, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
            }}
        >
            <Typography
                fontFamily={FONTS.display}
                fontWeight={700}
                fontSize="13px"
                color={primary}
            >
                {number}
            </Typography>
        </Box>
        <Typography
            fontFamily={FONTS.body}
            fontSize="14px"
            lineHeight="140%"
            color="text.primary"
        >
            {text}
        </Typography>
    </Stack>
);

// ─── Browser Tabs ────────────────────────────────────────────────────────────

const BrowserTabs: React.FC<{
    active: BrowserTab;
    onChange: (tab: BrowserTab) => void;
    primary: string;
}> = ({ active, onChange, primary }) => {
    const tabs: { key: BrowserTab; label: string }[] = [
        { key: "safari", label: "Safari" },
        { key: "chrome", label: "Chrome" },
        { key: "other", label: "Other" },
    ];

    return (
        <Stack
            direction="row"
            sx={{
                bgcolor: alpha(primary, 0.05),
                borderRadius: "12px",
                p: 0.5,
                animation: "fadeSlideUp 0.35s ease-out both",
            }}
        >
            {tabs.map((tab) => (
                <Box
                    key={tab.key}
                    onClick={() => onChange(tab.key)}
                    sx={{
                        flex: 1,
                        py: 1,
                        borderRadius: "10px",
                        bgcolor:
                            active === tab.key
                                ? "white"
                                : "transparent",
                        boxShadow:
                            active === tab.key
                                ? "0px 1px 4px rgba(0,0,0,0.08)"
                                : "none",
                        cursor: "pointer",
                        transition: "all 0.25s ease",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={active === tab.key ? 600 : 500}
                        fontSize="13px"
                        color={
                            active === tab.key
                                ? primary
                                : "text.secondary"
                        }
                    >
                        {tab.label}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
};

// ─── Step Cards ──────────────────────────────────────────────────────────────

interface StepProps {
    number: number;
    title: string;
    description: string;
    primary: string;
    mockup: React.ReactNode;
    delay: number;
}

const StepCard: React.FC<StepProps> = ({
    number,
    title,
    description,
    primary,
    mockup,
    delay,
}) => (
    <Stack
        sx={{
            ...CARD_STYLES,
            p: 0,
            overflow: "hidden",
            animation: "fadeSlideUp 0.4s ease-out both",
            animationDelay: `${0.05 + delay * 0.08}s`,
        }}
    >
        {/* Mockup area */}
        <Box
            sx={{
                bgcolor: alpha(primary, 0.03),
                px: 2.5,
                py: 2,
                display: "flex",
                justifyContent: "center",
            }}
        >
            {mockup}
        </Box>

        {/* Text content */}
        <Stack sx={{ p: 2 }} spacing={0.5} direction="row" alignItems="flex-start">
            <Box
                sx={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    bgcolor: alpha(primary, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    mr: 1.5,
                }}
            >
                <Typography
                    fontFamily={FONTS.display}
                    fontWeight={700}
                    fontSize="16px"
                    color={primary}
                >
                    {number}
                </Typography>
            </Box>
            <Stack spacing={0.25} flex={1}>
                <Typography
                    fontFamily={FONTS.display}
                    fontWeight={600}
                    fontSize="15px"
                    lineHeight="130%"
                    color="text.primary"
                >
                    {title}
                </Typography>
                <Typography
                    fontFamily={FONTS.body}
                    fontSize="13px"
                    lineHeight="150%"
                    color="text.secondary"
                >
                    {description}
                </Typography>
            </Stack>
        </Stack>
    </Stack>
);

// ─── CSS Mockups ─────────────────────────────────────────────────────────────

const SafariBottomBarMockup: React.FC<{ primary: string }> = ({ primary }) => (
    <Stack
        sx={{
            width: "220px",
            borderRadius: "14px",
            bgcolor: "white",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            overflow: "hidden",
        }}
    >
        {/* Fake page content */}
        <Box sx={{ height: "48px", bgcolor: alpha(primary, 0.06), px: 2, pt: 1.5 }}>
            <Box sx={{ width: "60%", height: "6px", borderRadius: "3px", bgcolor: alpha(primary, 0.12), mb: 0.75 }} />
            <Box sx={{ width: "80%", height: "5px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.06)" }} />
        </Box>
        {/* URL bar */}
        <Box sx={{ px: 1.5, py: 1 }}>
            <Box
                sx={{
                    bgcolor: "rgba(0,0,0,0.05)",
                    borderRadius: "8px",
                    py: 0.5,
                    px: 1.5,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Typography fontSize="10px" color="text.secondary" fontFamily={FONTS.body}>
                    yoursite.com
                </Typography>
            </Box>
        </Box>
        {/* Bottom bar with share highlighted */}
        <Stack
            direction="row"
            justifyContent="space-around"
            alignItems="center"
            sx={{ px: 2, py: 1, borderTop: "1px solid rgba(0,0,0,0.06)" }}
        >
            <Box sx={{ width: "18px", height: "18px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.1)" }} />
            <Box sx={{ width: "18px", height: "18px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.1)" }} />
            <Box
                sx={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "8px",
                    bgcolor: alpha(primary, 0.12),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                        "0%, 100%": { boxShadow: `0 0 0 0px ${alpha(primary, 0.3)}` },
                        "50%": { boxShadow: `0 0 0 6px ${alpha(primary, 0)}` },
                    },
                }}
            >
                <IosShareRounded sx={{ fontSize: "16px", color: primary }} />
            </Box>
            <Box sx={{ width: "18px", height: "18px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.1)" }} />
            <Box sx={{ width: "18px", height: "18px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.1)" }} />
        </Stack>
    </Stack>
);

const SafariTopBarMockup: React.FC<{ primary: string }> = ({ primary }) => (
    <Stack
        sx={{
            width: "220px",
            borderRadius: "14px",
            bgcolor: "white",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            overflow: "hidden",
        }}
    >
        {/* Top bar with URL + ··· menu */}
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 1.5, py: 1, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
        >
            <Box
                sx={{
                    flex: 1,
                    bgcolor: "rgba(0,0,0,0.05)",
                    borderRadius: "8px",
                    py: 0.5,
                    px: 1.5,
                    mr: 1,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Typography fontSize="10px" color="text.secondary" fontFamily={FONTS.body}>
                    yoursite.com
                </Typography>
            </Box>
            <Box
                sx={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "8px",
                    bgcolor: alpha(primary, 0.12),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                        "0%, 100%": { boxShadow: `0 0 0 0px ${alpha(primary, 0.3)}` },
                        "50%": { boxShadow: `0 0 0 6px ${alpha(primary, 0)}` },
                    },
                }}
            >
                <MoreHorizRounded sx={{ fontSize: "16px", color: primary }} />
            </Box>
        </Stack>
        {/* Fake page content */}
        <Box sx={{ height: "48px", px: 2, pt: 1.5 }}>
            <Box sx={{ width: "60%", height: "6px", borderRadius: "3px", bgcolor: alpha(primary, 0.12), mb: 0.75 }} />
            <Box sx={{ width: "80%", height: "5px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.06)" }} />
        </Box>
    </Stack>
);

const SafariShareStepMockup: React.FC<{ primary: string }> = ({ primary }) => (
    <Stack spacing={1.5} alignItems="center" width="100%">
        <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ transform: "scale(0.88)", transformOrigin: "center top" }}
        >
            <Stack spacing={0.75} alignItems="center">
                <SafariBottomBarMockup primary={primary} />
                <Typography
                    fontFamily={FONTS.body}
                    fontSize="11px"
                    fontWeight={500}
                    color="text.secondary"
                >
                    Bottom address bar
                </Typography>
            </Stack>
            <Stack spacing={0.75} alignItems="center">
                <SafariTopBarMockup primary={primary} />
                <Typography
                    fontFamily={FONTS.body}
                    fontSize="11px"
                    fontWeight={500}
                    color="text.secondary"
                >
                    Top address bar
                </Typography>
            </Stack>
        </Stack>
    </Stack>
);

const ChromeTopBarMockup: React.FC<{ primary: string }> = ({ primary }) => (
    <Stack
        sx={{
            width: "220px",
            borderRadius: "14px",
            bgcolor: "white",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            overflow: "hidden",
        }}
    >
        {/* Top bar with share */}
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 1.5, py: 1, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
        >
            <Box
                sx={{
                    flex: 1,
                    bgcolor: "rgba(0,0,0,0.05)",
                    borderRadius: "8px",
                    py: 0.5,
                    px: 1.5,
                    mr: 1,
                }}
            >
                <Typography fontSize="10px" color="text.secondary" fontFamily={FONTS.body}>
                    yoursite.com
                </Typography>
            </Box>
            <Box
                sx={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "8px",
                    bgcolor: alpha(primary, 0.12),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                        "0%, 100%": { boxShadow: `0 0 0 0px ${alpha(primary, 0.3)}` },
                        "50%": { boxShadow: `0 0 0 6px ${alpha(primary, 0)}` },
                    },
                }}
            >
                <IosShareRounded sx={{ fontSize: "16px", color: primary }} />
            </Box>
        </Stack>
        {/* Fake page content */}
        <Box sx={{ height: "56px", px: 2, pt: 1.5 }}>
            <Box sx={{ width: "60%", height: "6px", borderRadius: "3px", bgcolor: alpha(primary, 0.12), mb: 0.75 }} />
            <Box sx={{ width: "80%", height: "5px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.06)", mb: 0.5 }} />
            <Box sx={{ width: "45%", height: "5px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.06)" }} />
        </Box>
    </Stack>
);

const OtherBrowserMenuMockup: React.FC<{ primary: string }> = ({ primary }) => (
    <Stack
        sx={{
            width: "220px",
            borderRadius: "14px",
            bgcolor: "white",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            overflow: "hidden",
        }}
    >
        {/* Top bar with menu */}
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 1.5, py: 1, borderBottom: "1px solid rgba(0,0,0,0.06)" }}
        >
            <Box
                sx={{
                    flex: 1,
                    bgcolor: "rgba(0,0,0,0.05)",
                    borderRadius: "8px",
                    py: 0.5,
                    px: 1.5,
                    mr: 1,
                }}
            >
                <Typography fontSize="10px" color="text.secondary" fontFamily={FONTS.body}>
                    yoursite.com
                </Typography>
            </Box>
            <Box
                sx={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "8px",
                    bgcolor: alpha(primary, 0.12),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                        "0%, 100%": { boxShadow: `0 0 0 0px ${alpha(primary, 0.3)}` },
                        "50%": { boxShadow: `0 0 0 6px ${alpha(primary, 0)}` },
                    },
                }}
            >
                <MoreHorizRounded sx={{ fontSize: "16px", color: primary }} />
            </Box>
        </Stack>
        {/* Fake page content */}
        <Box sx={{ height: "56px", px: 2, pt: 1.5 }}>
            <Box sx={{ width: "60%", height: "6px", borderRadius: "3px", bgcolor: alpha(primary, 0.12), mb: 0.75 }} />
            <Box sx={{ width: "80%", height: "5px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.06)", mb: 0.5 }} />
            <Box sx={{ width: "45%", height: "5px", borderRadius: "3px", bgcolor: "rgba(0,0,0,0.06)" }} />
        </Box>
    </Stack>
);

const ShareSheetMockup: React.FC<{ primary: string }> = ({ primary }) => (
    <Stack
        sx={{
            width: "220px",
            borderRadius: "14px",
            bgcolor: "white",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            overflow: "hidden",
            p: 1.5,
        }}
        spacing={0.75}
    >
        {/* Fake share sheet rows */}
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1, py: 0.75, borderRadius: "8px", bgcolor: "rgba(0,0,0,0.03)" }}
        >
            <Box sx={{ width: "16px", height: "16px", borderRadius: "4px", bgcolor: "rgba(0,0,0,0.1)" }} />
            <Typography fontSize="11px" color="text.secondary" fontFamily={FONTS.body}>
                Copy
            </Typography>
        </Stack>
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1, py: 0.75, borderRadius: "8px", bgcolor: "rgba(0,0,0,0.03)" }}
        >
            <Box sx={{ width: "16px", height: "16px", borderRadius: "4px", bgcolor: "rgba(0,0,0,0.1)" }} />
            <Typography fontSize="11px" color="text.secondary" fontFamily={FONTS.body}>
                Add to Reading List
            </Typography>
        </Stack>
        {/* Highlighted row */}
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
                px: 1,
                py: 0.75,
                borderRadius: "8px",
                bgcolor: alpha(primary, 0.08),
                border: `1.5px solid ${alpha(primary, 0.2)}`,
            }}
        >
            <AddBoxRounded sx={{ fontSize: "16px", color: primary }} />
            <Typography
                fontSize="11px"
                fontWeight={600}
                color={primary}
                fontFamily={FONTS.body}
            >
                Add to Home Screen
            </Typography>
        </Stack>
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1, py: 0.75, borderRadius: "8px", bgcolor: "rgba(0,0,0,0.03)" }}
        >
            <Box sx={{ width: "16px", height: "16px", borderRadius: "4px", bgcolor: "rgba(0,0,0,0.1)" }} />
            <Typography fontSize="11px" color="text.secondary" fontFamily={FONTS.body}>
                Add to Favorites
            </Typography>
        </Stack>
    </Stack>
);

const AddConfirmMockup: React.FC<{
    primary: string;
    brandName?: string;
    brandLogo?: string | null;
}> = ({ primary, brandName, brandLogo }) => (
    <Stack
        sx={{
            width: "220px",
            borderRadius: "14px",
            bgcolor: "white",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
            overflow: "hidden",
            p: 2,
        }}
        spacing={1.5}
        alignItems="center"
    >
        {/* App icon — real logo or gradient fallback */}
        {brandLogo ? (
            <Box
                component="img"
                src={brandLogo}
                alt="App icon"
                sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    objectFit: "cover",
                }}
            />
        ) : (
            <Box
                sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    background: `linear-gradient(135deg, ${lighten(primary, 0.15)}, ${primary})`,
                }}
            />
        )}
        <Typography fontFamily={FONTS.body} fontSize="12px" fontWeight={600} color="text.primary">
            {brandName ? <>{brandName} <span className="no-translation">ADMIN</span></> : <>Your App <span className="no-translation">ADMIN</span></>}
        </Typography>
        <Typography fontFamily={FONTS.body} fontSize="10px" color="text.secondary">
            {window.location.hostname}
        </Typography>
        {/* Buttons row */}
        <Stack direction="row" spacing={1} width="100%">
            <Box
                sx={{
                    flex: 1,
                    py: 0.75,
                    borderRadius: "8px",
                    bgcolor: "rgba(0,0,0,0.05)",
                    textAlign: "center",
                }}
            >
                <Typography fontSize="11px" color="text.secondary" fontFamily={FONTS.body}>
                    Cancel
                </Typography>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    py: 0.75,
                    borderRadius: "8px",
                    bgcolor: alpha(primary, 0.1),
                    textAlign: "center",
                    border: `1.5px solid ${alpha(primary, 0.2)}`,
                }}
            >
                <Typography
                    fontSize="11px"
                    fontWeight={600}
                    color={primary}
                    fontFamily={FONTS.body}
                >
                    Add
                </Typography>
            </Box>
        </Stack>
    </Stack>
);

// ─── Browser-Specific Step Sets ──────────────────────────────────────────────

interface BrandDisplayInfo {
    name?: string;
    logo?: string | null;
}

const SafariSteps: React.FC<{ primary: string; brand: BrandDisplayInfo }> = ({ primary, brand }) => (
    <Stack spacing={2}>
        <Typography
            fontFamily={FONTS.body}
            fontWeight={600}
            fontSize="11px"
            color="text.secondary"
            textTransform="uppercase"
            letterSpacing="0.6px"
            sx={{
                animation: "fadeSlideUp 0.35s ease-out both",
            }}
        >
            Safari Steps
        </Typography>
        <StepCard
            number={1}
            title="Find the Share button"
            description="Its location depends on your Safari layout. Look for the share icon at the bottom, or tap ··· next to the address bar at the top"
            primary={primary}
            mockup={<SafariShareStepMockup primary={primary} />}
            delay={0}
        />
        <StepCard
            number={2}
            title='Find "Add to Home Screen"'
            description='In the share menu, scroll down until you see "Add to Home Screen" and tap it'
            primary={primary}
            mockup={<ShareSheetMockup primary={primary} />}
            delay={1}
        />
        <StepCard
            number={3}
            title='Tap "Add"'
            description="Confirm the name and tap Add in the top-right corner"
            primary={primary}
            mockup={<AddConfirmMockup primary={primary} brandName={brand.name} brandLogo={brand.logo} />}
            delay={2}
        />
        <DoneNote primary={primary} delay={3} />
    </Stack>
);

const ChromeSteps: React.FC<{ primary: string; brand: BrandDisplayInfo }> = ({ primary, brand }) => (
    <Stack spacing={2}>
        <Typography
            fontFamily={FONTS.body}
            fontWeight={600}
            fontSize="11px"
            color="text.secondary"
            textTransform="uppercase"
            letterSpacing="0.6px"
            sx={{
                animation: "fadeSlideUp 0.35s ease-out both",
            }}
        >
            Chrome Steps
        </Typography>
        <StepCard
            number={1}
            title="Tap the Share button"
            description="Find it in the top-right area of the Chrome toolbar"
            primary={primary}
            mockup={<ChromeTopBarMockup primary={primary} />}
            delay={0}
        />
        <StepCard
            number={2}
            title='Find "Add to Home Screen"'
            description="Scroll down in the share menu until you see it"
            primary={primary}
            mockup={<ShareSheetMockup primary={primary} />}
            delay={1}
        />
        <StepCard
            number={3}
            title='Tap "Add"'
            description="Confirm the name and tap Add to finish"
            primary={primary}
            mockup={<AddConfirmMockup primary={primary} brandName={brand.name} brandLogo={brand.logo} />}
            delay={2}
        />
        <DoneNote primary={primary} delay={3} />
    </Stack>
);

const OtherBrowserSteps: React.FC<{ primary: string; brand: BrandDisplayInfo }> = ({ primary, brand }) => (
    <Stack spacing={2}>
        <Typography
            fontFamily={FONTS.body}
            fontWeight={600}
            fontSize="11px"
            color="text.secondary"
            textTransform="uppercase"
            letterSpacing="0.6px"
            sx={{
                animation: "fadeSlideUp 0.35s ease-out both",
            }}
        >
            Browser Steps
        </Typography>
        <StepCard
            number={1}
            title="Tap the menu button"
            description='Look for the "..." or menu icon in the toolbar'
            primary={primary}
            mockup={<OtherBrowserMenuMockup primary={primary} />}
            delay={0}
        />
        <StepCard
            number={2}
            title='Find "Add to Home Screen"'
            description="Look through the menu options for it"
            primary={primary}
            mockup={<ShareSheetMockup primary={primary} />}
            delay={1}
        />
        <StepCard
            number={3}
            title='Tap "Add"'
            description="Confirm the name and tap Add to finish"
            primary={primary}
            mockup={<AddConfirmMockup primary={primary} brandName={brand.name} brandLogo={brand.logo} />}
            delay={2}
        />
        <DoneNote primary={primary} delay={3} />
    </Stack>
);

const DoneNote: React.FC<{ primary: string; delay: number }> = ({ primary, delay }) => (
    <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
            px: 2,
            py: 1.5,
            borderRadius: "12px",
            bgcolor: alpha(primary, 0.04),
            animation: "fadeSlideUp 0.4s ease-out both",
            animationDelay: `${0.05 + delay * 0.08}s`,
        }}
    >
        <TouchAppRounded sx={{ color: primary, fontSize: "20px" }} />
        <Typography
            fontFamily={FONTS.body}
            fontSize="13px"
            lineHeight="150%"
            color="text.secondary"
        >
            That's it! The app icon will appear on your home screen.
        </Typography>
    </Stack>
);

export default IOSInstallGuide;
